/**
 * Sale Order PDF Generation Service
 */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';

/**
 * Generate HTML for Sale Order PDF
 * @param {Object} saleOrder - Sale order data
 * @param {Object} storeInfo - Company/store information
 * @param {string} language - Language code (en, fr, nl)
 * @returns {string} HTML string for PDF generation
 */
export const generateSaleOrderPdfHtml = (saleOrder, storeInfo, language = 'en') => {
  const {
    SaleOrderNumber,
    SaleOrderDate,
    Customer,
    LineItems = [],
    CustomText = '',
    VATPercentage = 21,
    Status = 'Draft',
    PaymentStatus = 'Not Paid',
    PaidAmount = 0,
    DiscountAmount = 0,
    IsTvaIncluded = true,
    PaymentMethod = '',
    PaymentMethods = [],
    OutstandingInvoices = [],
  } = saleOrder;

  // Calculate totals
  const totalQuantity = LineItems.reduce((sum, item) => sum + (Number(item.Quantity) || 0), 0);
  const totalExcl = LineItems.reduce((sum, item) => sum + Number(item.TotalExcl || 0), 0);
  const totalIncl = LineItems.reduce((sum, item) => sum + Number(item.TotalIncl || 0), 0);
  
  // Calculate discount and final totals based on price type
  const discountAmount = DiscountAmount || 0;
  let subtotalAfterDiscount = 0;
  let vatAmount = 0;
  let finalTotal = 0;
  
  if (IsTvaIncluded) {
    // Prices include VAT
    subtotalAfterDiscount = totalIncl - discountAmount;
    finalTotal = subtotalAfterDiscount;
    vatAmount = VATPercentage > 0 ? (subtotalAfterDiscount / (1 + VATPercentage / 100)) * (VATPercentage / 100) : 0;
  } else {
    // Prices exclude VAT
    subtotalAfterDiscount = totalExcl - discountAmount;
    vatAmount = VATPercentage > 0 ? subtotalAfterDiscount * (VATPercentage / 100) : 0;
    finalTotal = subtotalAfterDiscount + vatAmount;
  }
  
  // Calculate outstanding total
  const outstandingTotal = OutstandingInvoices && OutstandingInvoices.length > 0 
    ? OutstandingInvoices.reduce((sum, inv) => sum + parseFloat(inv.DueAmount || 0), 0) 
    : 0;
  const grandTotalWithOutstanding = finalTotal + outstandingTotal;
  
  const dueAmount = finalTotal - PaidAmount;
  
  const lineItemsHtml = LineItems.map((item, index) => {
    const quantity = Number(item.Quantity) || 0;
    const unitPrice = Number(item.UnitPrice) || 0;
    const totalExcl = Number(item.TotalExcl) || (quantity * unitPrice);
    const totalIncl = Number(item.TotalIncl) || (totalExcl * (1 + VATPercentage / 100));
    const rowClass = index % 2 === 1 ? 'alternate-row' : '';
    
    // Use SKUBarCode field or fallback to SKU/BarCode
    const skuBarCode = item.SKUBarCode || item.skuBarCode || item.SkuBarCode || '';
    const sku = item.SKU || item.sku || item.ProductSKU || item.Sku || '';
    const barcode = item.BarCode || item.barCode || item.Barcode || item.ProductBarCode || '';
    const skuDisplay = skuBarCode || sku || barcode || '-';

    return `
      <tr class="${rowClass}">
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 8pt;">${index + 1}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: center; font-size: 8pt;">${skuDisplay}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 8pt;">
          ${item.Name || item.ProductName || 'Unknown Product'}
          ${item.Description ? `<br/><span style="color: #555; font-size: 7pt;">${item.Description}</span>` : ''}
        </td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: center; font-size: 8pt;">${quantity}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 8pt;">€${unitPrice.toFixed(2)}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 8pt;">€${totalIncl.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  // Format date
  const formatDate = (date) => {
    if (!date) return new Date().toLocaleDateString(language);
    // Handle date string properly - don't create Date object which causes timezone issues
    const dateStr = typeof date === 'string' ? date.split('T')[0] : date;
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  };

  // Get labels based on language
  const labels = getLabels(language);

  // Format customer address without country
  const customerAddress = [
    Customer?.Street || '',
    Customer?.City && Customer?.PostCode ? `${Customer.PostCode} ${Customer.City}` : (Customer?.City || Customer?.PostCode || '')
  ].filter(Boolean).join(', ');

  // Format store address - separate lines for street and city/postcode
  const storeStreet = storeInfo?.Address || storeInfo?.address || '';
  const storeCityPostcode = storeInfo?.City && storeInfo?.PostCode ? `${storeInfo.PostCode} ${storeInfo.City}` : 
    (storeInfo?.city && storeInfo?.postCode ? `${storeInfo.postCode} ${storeInfo.city}` : 
    (storeInfo?.City || storeInfo?.city || storeInfo?.PostCode || storeInfo?.postCode || ''));
  
  // Get company phone number
  const storePhone = storeInfo?.PhoneNumber || storeInfo?.phoneNumber || storeInfo?.Phone || storeInfo?.phone || '';

  const html = `
<!DOCTYPE html>
<html lang="${language}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${labels.saleOrder} - ${SaleOrderNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: white;
      width: 210mm;
      overflow-x: hidden;
    }
    body { 
      font-family: 'Arial', 'Helvetica', sans-serif; 
      font-size: 9pt; 
      color: #000; 
      line-height: 1.4;
    }
    .page { 
      width: 210mm;
      min-height: 297mm;
      height: 297mm;
      padding: 8mm 8mm 8mm 8mm;
      margin: 0;
      background: white; 
      position: relative;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    .content-wrapper {
      flex: 1 1 auto;
      min-height: 0;
    }
    /* Row 1: Document title (left) + Date/Order# (right, narrow column) */
    .doc-title-bar {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 20px;
      margin-bottom: 12px;
    }
    .doc-title {
      font-size: 24pt;
      font-weight: bold;
      color: #bbb;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      opacity: 0.75;
      flex: 1;
      padding-top: 2px;
    }
    .doc-meta {
      width: 25%;
      flex: 0 0 25%;
      text-align: left;
      font-size: 8.5pt;
      color: #333;
      line-height: 1.5;
      margin-left: auto;
    }
    .doc-meta .order-date {
      font-size: 8.5pt;
      color: #333;
    }
    .doc-meta .order-number {
      font-size: 11pt;
      font-weight: bold;
      color: #000;
      letter-spacing: 0.5px;
    }
    /* Row 2: Company (left, fills remaining) + Customer (right, same width as doc-meta) */
    .parties-row {
      display: flex;
      justify-content: space-between;
      gap: 20px;
      margin-top: 6px;
      margin-bottom: 18px;
      padding-top: 10px;
      border-top: 1px solid #d0d0d0;
      page-break-inside: avoid;
    }
    .party-block {
      font-size: 8.5pt;
      color: #333;
      line-height: 1.5;
      text-align: left;
    }
    .party-block.company {
      flex: 1;
    }
    .party-block.customer {
      /* Same fixed width as .doc-meta so both blocks' left edges align. */
      width: 25%;
      flex: 0 0 25%;
      margin-left: auto;
    }
    .party-label {
      font-size: 8pt;
      font-weight: 600;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }
    .party-block .party-name {
      font-size: 10.5pt;
      font-weight: bold;
      color: #000;
      margin-bottom: 3px;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin-top: 4px;
      margin-bottom: 12px;
      page-break-inside: auto;
      table-layout: fixed;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead { display: table-header-group; }
    th { 
      background: #f5f5f5; 
      color: #000; 
      padding: 8px; 
      text-align: left; 
      font-weight: 600; 
      font-size: 8.5pt;
      border-bottom: 2px solid #000;
      border-top: 2px solid #000;
    }
    .alternate-row { background-color: #fafafa; }
    td { 
      padding: 6px 8px; 
      border-bottom: 1px solid #eee; 
      font-size: 8.5pt;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    .totals-wrapper {
      page-break-inside: avoid;
      margin-bottom: 12px;
    }
    .totals { 
      margin-left: auto; 
      width: 45%; 
      border-top: 2px solid #000;
      padding-top: 8px;
    }
    .totals-row { 
      display: flex; 
      justify-content: space-between; 
      padding: 4px 0; 
      font-size: 9pt;
    }
    .totals-row.grand-total { 
      font-size: 10pt; 
      font-weight: bold; 
      color: #000; 
      border-top: 2px solid #000; 
      margin-top: 4px; 
      padding-top: 8px; 
    }
    .custom-text { 
      margin-top: 12px; 
      padding: 10px; 
      border: 1px solid #ccc;
      border-radius: 4px;
      page-break-inside: avoid;
      background: #fafafa;
    }
    .custom-text-label { 
      font-weight: 600; 
      color: #000; 
      margin-bottom: 4px;
      font-size: 9pt;
    }
    .notes-section {
      margin-top: 20px;
      margin-bottom: 15px;
      page-break-inside: avoid;
    }
    .footer { 
      flex-shrink: 0;
      margin-top: auto;
      padding-top: 8px;
      padding-bottom: 0;
      text-align: center; 
      font-size: 8.5pt; 
      color: #666;
      border-top: 1px solid #ddd;
      page-break-inside: avoid;
    }
    .footer-thanks {
      font-weight: 600;
      font-size: 9pt;
      color: #444;
    }
    .footer-page-num {
      position: absolute;
      right: 8mm;
      bottom: 4mm;
      font-size: 8pt;
      color: #666;
      z-index: 5;
    }
    @page {
      size: A4;
      margin: 0;
    }
    @media print {
      html, body { width: 210mm; height: 297mm; }
      .page { 
        margin: 0; 
        box-shadow: none;
        min-height: 297mm;
        height: 297mm;
        display: flex;
        flex-direction: column;
      }
      .content-wrapper { flex: 1 1 auto; min-height: 0; }
      .footer { margin-top: auto; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid !important; }
      .totals-wrapper,
      .custom-text,
      .notes-section,
      .footer { page-break-inside: avoid; }
      .footer-page-num { display: block !important; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="content-wrapper">
      <!-- Row 1: Document title (left) + Date/Order# (right) -->
      <div class="doc-title-bar">
        <div class="doc-title">${labels.saleOrder}</div>
        <div class="doc-meta">
          <div class="order-date">${labels.date}: ${formatDate(SaleOrderDate)}</div>
          <div class="order-number">${SaleOrderNumber || 'N/A'}</div>
        </div>
      </div>

      <!-- Row 2: Company (left) + Customer (right, content left-aligned) -->
      <div class="parties-row">
        <div class="party-block company">
          <div class="party-label">${labels.from || 'From'}</div>
          <div class="party-name">${storeInfo?.CompanyName || storeInfo?.name || storeInfo?.StoreName || storeInfo?.storeName || 'Company Name'}</div>
          ${storeStreet ? `<div>${storeStreet}</div>` : ''}
          ${storeCityPostcode ? `<div>${storeCityPostcode}</div>` : ''}
          ${storePhone ? `<div>${labels.phone}: ${storePhone}</div>` : ''}
        </div>
        <div class="party-block customer">
          <div class="party-label">${labels.billTo || labels.customer}</div>
          <div class="party-name">${Customer?.CompanyName || Customer?.Name || 'Unknown Customer'}</div>
          ${Customer?.VATNumber ? `<div>${labels.vat}: ${Customer.VATNumber}</div>` : ''}
          ${customerAddress ? `<div>${customerAddress}</div>` : ''}
          ${Customer?.PhoneNumber || Customer?.MobileNumber ? `<div>${labels.phone}: ${Customer.PhoneNumber || Customer.MobileNumber}</div>` : ''}
        </div>
      </div>

      <!-- Line Items Table -->
      <table>
        <thead>
          <tr>
            <th style="width: 5%;">#</th>
            <th style="width: 14%; text-align: center;">${labels.sku}</th>
            <th style="width: 36%;">${labels.description}</th>
            <th style="width: 11%; text-align: center;">${labels.quantity}</th>
            <th style="width: 17%; text-align: right;">${labels.unitPrice}</th>
            <th style="width: 17%; text-align: right;">${labels.total}</th>
          </tr>
        </thead>
        <tbody>
          ${lineItemsHtml}
          <tr style="background-color: #e8e8e8; font-weight: 600;">
            <td style="padding: 8px; border-bottom: 2px solid #000; font-size: 8.5pt;" colspan="2"></td>
            <td style="padding: 8px; border-bottom: 2px solid #000; font-size: 8.5pt;">${labels.total}</td>
            <td style="padding: 8px; border-bottom: 2px solid #000; text-align: center; font-size: 8.5pt;">${totalQuantity}</td>
            <td style="padding: 8px; border-bottom: 2px solid #000; font-size: 8.5pt;"></td>
            <td style="padding: 8px; border-bottom: 2px solid #000; text-align: right; font-size: 8.5pt;">€${(IsTvaIncluded ? totalIncl : finalTotal).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <!-- Totals -->
      <div class="totals-wrapper">
        <div class="totals">
          <div class="totals-row">
            <span>${labels.subtotal}:</span>
            <span>€${(IsTvaIncluded ? totalIncl : totalExcl).toFixed(2)}</span>
          </div>
          ${discountAmount > 0 ? `
            <div class="totals-row" style="color: #ff9800; font-weight: 600;">
              <span>${labels.discount}:</span>
              <span>-€${discountAmount.toFixed(2)}</span>
            </div>
            <div class="totals-row" style="font-weight: 600;">
              <span>${labels.subtotalAfterDiscount}:</span>
              <span>€${subtotalAfterDiscount.toFixed(2)}</span>
            </div>
          ` : ''}
          ${VATPercentage > 0 ? `
            <div class="totals-row">
              <span>${labels.vat} (${VATPercentage}%):</span>
              <span>€${vatAmount.toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="totals-row" style="font-weight: 600; ${outstandingTotal > 0 ? 'border-top: 1px solid #ddd; padding-top: 8px; margin-top: 4px;' : ''}">
            <span>${labels.total}:</span>
            <span>€${finalTotal.toFixed(2)}</span>
          </div>
          ${PaidAmount > 0 ? `
            <div class="totals-row" style="color: #10b981; font-weight: 600; border-top: 1px solid #ddd; padding-top: 8px; margin-top: 4px;">
              <span>${labels.paidAmount}:</span>
              <span>-€${PaidAmount.toFixed(2)}</span>
            </div>
            <div class="totals-row" style="font-weight: 700; font-size: 10pt; color: ${dueAmount > 0 ? '#f59e0b' : '#10b981'};">
              <span>${labels.dueAmount}:</span>
              <span>€${dueAmount.toFixed(2)}</span>
            </div>
          ` : ''}
        </div>
      </div>

      ${OutstandingInvoices && OutstandingInvoices.length > 0 ? `
      <!-- Outstanding Invoices Reference -->
      <div style="margin-top: 14px; margin-bottom: 10px; page-break-inside: avoid;">
        <div style="font-size: 9pt; font-weight: 600; color: #e65100; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 6px; padding-bottom: 4px; border-bottom: 1.5px solid #ffcc80;">
          ${labels.outstandingInvoices || 'Outstanding Invoices Reference'}
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
          <thead>
            <tr style="background: #fff3e0;">
              <th style="padding: 5px 8px; text-align: left; border-bottom: 1px solid #ffcc80; border-top: 1px solid #ffcc80; font-weight: 600; color: #bf360c; width: 30%;">${labels.documentRef || 'Document #'}</th>
              <th style="padding: 5px 8px; text-align: left; border-bottom: 1px solid #ffcc80; border-top: 1px solid #ffcc80; font-weight: 600; color: #bf360c; width: 20%;">${labels.documentType || 'Type'}</th>
              <th style="padding: 5px 8px; text-align: left; border-bottom: 1px solid #ffcc80; border-top: 1px solid #ffcc80; font-weight: 600; color: #bf360c; width: 25%;">${labels.date}</th>
              <th style="padding: 5px 8px; text-align: right; border-bottom: 1px solid #ffcc80; border-top: 1px solid #ffcc80; font-weight: 600; color: #bf360c; width: 25%;">${labels.dueAmount}</th>
            </tr>
          </thead>
          <tbody>
            ${OutstandingInvoices.map((inv, idx) => `
              <tr style="background: ${idx % 2 === 1 ? '#fff8f0' : '#ffffff'};">
                <td style="padding: 4px 8px; border-bottom: 1px solid #fff3e0; font-weight: 600;">${inv.DocumentNumber || '\u2014'}</td>
                <td style="padding: 4px 8px; border-bottom: 1px solid #fff3e0;">${inv.DocumentType || 'Invoice'}</td>
                <td style="padding: 4px 8px; border-bottom: 1px solid #fff3e0;">${inv.DocumentDate ? inv.DocumentDate.split('T')[0] : '\u2014'}</td>
                <td style="padding: 4px 8px; border-bottom: 1px solid #fff3e0; text-align: right; font-weight: 600;">\u20ac${parseFloat(inv.DueAmount || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
            <tr style="background: #fff3e0;">
              <td colspan="3" style="padding: 5px 8px; border-top: 1.5px solid #ffcc80; font-weight: 700; font-size: 8.5pt; text-align: right;">${labels.total}:</td>
              <td style="padding: 5px 8px; border-top: 1.5px solid #ffcc80; font-weight: 700; font-size: 8.5pt; text-align: right;">\u20ac${OutstandingInvoices.reduce((sum, inv) => sum + parseFloat(inv.DueAmount || 0), 0).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Grand Total -->
      <div class="totals-wrapper">
        <div class="totals">
          <div class="totals-row grand-total">
            <span>${labels.grandTotal}:</span>
            <span>€${grandTotalWithOutstanding.toFixed(2)}</span>
          </div>
        </div>
      </div>
      ` : ''}

      <!-- Payment Method -->
      ${(PaymentMethod || (PaymentMethods && PaymentMethods.length > 0)) ? `
        <div class="totals-wrapper">
          <div class="custom-text">
            <div class="custom-text-label">${labels.paymentMethod}:</div>
            <div style="font-size: 8.5pt; margin-top: 4px;">
              ${PaymentMethods && PaymentMethods.length > 0 
                ? PaymentMethods.map(pm => `${pm.name || pm.label}: €${Number(pm.amount || 0).toFixed(2)}`).join('<br/>') 
                : PaymentMethod}
            </div>
          </div>
        </div>
      ` : ''}

      <!-- Notes -->
      ${CustomText ? `
        <div class="notes-section">
          <div class="custom-text">
            <div class="custom-text-label">${labels.notes}:</div>
            <div>${CustomText}</div>
          </div>
        </div>
      ` : ''}
    </div>

    <!-- Footer -->
    <div class="footer">
      <div class="footer-thanks">${labels.thankYou}</div>
    </div>
  </div>
</body>
</html>
  `;

  return html;
};

/**
 * Generate and preview PDF using html2canvas and jsPDF (same as invoice)
 */
export const generateAndPreviewSaleOrderPDF = async (saleOrder, storeInfo, language = 'en', t = (key) => key) => {
  try {
    // Check if store info is loaded
    if (!storeInfo) {

      toast.error(t('store_info_not_loaded') || 'Store information not loaded. Please refresh the page.');
      return false;
    }

    // Generate HTML
    const filledTemplate = generateSaleOrderPdfHtml(saleOrder, storeInfo, language);

    // Create temporary div
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = filledTemplate;
    
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-99999px';
    tempDiv.style.top = '0';
    tempDiv.style.width = '794px';   // A4 width at 96 DPI
    tempDiv.style.height = '1123px'; // A4 height at 96 DPI
    tempDiv.style.background = '#ffffff';
    tempDiv.style.overflow = 'hidden';
    document.body.appendChild(tempDiv);

    // Wait for images to load (if any)
    const images = tempDiv.getElementsByTagName('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(), 3000);
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeout);
          resolve();
        };
      });
    });

    await Promise.all(imagePromises);

    // Force layout: flex column so footer sits at the very bottom
    const containerEl = tempDiv.querySelector('.page');
    if (containerEl) {
      containerEl.style.height = '1123px';
      containerEl.style.minHeight = '1123px';
      containerEl.style.display = 'flex';
      containerEl.style.flexDirection = 'column';
      containerEl.style.overflow = 'hidden';
      containerEl.style.paddingBottom = '20px';
      containerEl.style.marginBottom = '0';
      const cw = containerEl.querySelector('.content-wrapper');
      if (cw) {
        cw.style.flex = '1 1 auto';
        cw.style.minHeight = '0';
      }
      const ftr = containerEl.querySelector('.footer');
      if (ftr) {
        ftr.style.marginTop = 'auto';
        ftr.style.paddingBottom = '10px';
        ftr.style.marginBottom = '0';
      }
    }

    // Wait for images/fonts to settle
    await new Promise(resolve => setTimeout(resolve, 120));

    // Generate canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 1.5,  // Reduced from 2 to 1.5 for smaller file size while maintaining good quality
      useCORS: true,
      logging: false,
      width: 794,
      height: 1123,
      windowWidth: 794,
      windowHeight: 1123,
      backgroundColor: '#ffffff',
    });

    document.body.removeChild(tempDiv);

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Invalid canvas generated');
    }

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    // Open in new tab
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    
    return true;
  } catch (error) {

    
    if (error.message.includes('Invalid canvas')) {
      toast.error(t('failed_to_render_sale_order') || 'Failed to render sale order');
    } else if (error.message.includes('html2canvas')) {
      toast.error(t('failed_to_convert_html') || 'Failed to convert HTML');
    } else {
      toast.error(t('failed_to_generate_pdf') || 'Failed to generate PDF. Please try again.');
    }
    return false;
  }
};

/**
 * Get labels for different languages
 */
function getLabels(language) {
  const translations = {
    en: {
      saleOrder: 'Sale Order',
      saleOrderNumber: 'Sale Order #',
      date: 'Date',
      customer: 'Customer Details',
      from: 'From',
      billTo: 'Bill To',
      sku: 'SKU/Bar Code',
      description: 'Description',
      quantity: 'Quantity',
      unitPrice: 'Unit Price',
      total: 'Total',
      totalQuantity: 'Total Quantity',
      subtotal: 'Subtotal',
      subtotalAfterDiscount: 'Subtotal After Discount',
      vat: 'VAT',
      discount: 'Discount',
      grandTotal: 'Grand Total',
      paidAmount: 'Paid Amount',
      dueAmount: 'Due Amount',
      notes: 'Notes',
      paymentMethod: 'Payment Method',
      phone: 'Phone',
      email: 'Email',
      status: 'Status',
      paymentStatus: 'Payment Status',
      thankYou: 'Thank you for your business!',
      page: 'Page',
      outstandingInvoices: 'Outstanding Invoices Reference',
      outstandingReferences: 'Outstanding',
      documentRef: 'Document #',
      documentType: 'Type',
    },
    fr: {
      saleOrder: 'Bon de Commande',
      saleOrderNumber: 'N° Commande',
      date: 'Date',
      customer: 'Détails Client',
      from: 'De',
      billTo: 'Facturer à',
      sku: 'SKU/Code-barres',
      description: 'Description',
      quantity: 'Quantité',
      unitPrice: 'Prix Unitaire',
      total: 'Total',
      totalQuantity: 'Quantité Totale',
      subtotal: 'Sous-total',
      subtotalAfterDiscount: 'Sous-total Après Remise',
      vat: 'TVA',
      discount: 'Remise',
      grandTotal: 'Total Général',
      paidAmount: 'Montant Payé',
      dueAmount: 'Montant Dû',
      notes: 'Notes',
      paymentMethod: 'Méthode de Paiement',
      phone: 'Téléphone',
      email: 'Email',
      status: 'Statut',
      paymentStatus: 'Statut de Paiement',
      thankYou: 'Merci pour votre confiance!',
      page: 'Page',
      outstandingInvoices: 'R\u00e9f\u00e9rences Factures Impay\u00e9es',
      outstandingReferences: 'Impay\u00e9s',
      documentRef: 'Document N\u00b0',
      documentType: 'Type',
    },
    nl: {
      saleOrder: 'Verkooporder',
      saleOrderNumber: 'Order Nr.',
      date: 'Datum',
      customer: 'Klantgegevens',
      from: 'Van',
      billTo: 'Factuur aan',
      sku: 'SKU/Barcode',
      description: 'Beschrijving',
      quantity: 'Aantal',
      unitPrice: 'Eenheidsprijs',
      total: 'Totaal',
      totalQuantity: 'Totaal Aantal',
      subtotal: 'Subtotaal',
      subtotalAfterDiscount: 'Subtotaal Na Korting',
      vat: 'BTW',
      discount: 'Korting',
      grandTotal: 'Totaal',
      paidAmount: 'Betaald Bedrag',
      dueAmount: 'Verschuldigd Bedrag',
      notes: 'Opmerkingen',
      paymentMethod: 'Betaalmethode',
      phone: 'Telefoon',
      email: 'Email',
      status: 'Status',
      paymentStatus: 'Betalingsstatus',
      thankYou: 'Bedankt voor uw vertrouwen!',
      page: 'Pagina',
      outstandingInvoices: 'Referentie Openstaande Facturen',
      outstandingReferences: 'Openstaand',
      documentRef: 'Document Nr.',
      documentType: 'Type',
    },
  };

  return translations[language] || translations.en;
}
